/**
 * CreateObjectCommand
 *
 * Records creating a SceneObject (and optionally descendants, e.g. a pages
 * template or an imported multi-object GLB). Undo deletes the whole tree;
 * redo recreates it from the serialized data captured at construction.
 *
 * ## How parent identity survives undo/redo cycles
 *
 * A compound create has two sources of parent identity:
 *
 * 1. **External parent** (`this.parentRef`) — the parent of the top-level
 *    created object. It lives *outside* the created set, but it is NOT
 *    safe to hold as a raw SceneObject: hosts bundle this command with a
 *    lazily-created rig wrap (CompositeCommand [wrapCmd, createCmd]), and
 *    the wrap's undo DELETES an emptied rig while its redo creates a fresh
 *    instance. The parent is therefore held as a shared identity ref from
 *    the scene's `objectIdentityRegistry` — the wrap's rewrap rebinds the
 *    same ref, so redo resolves the LIVE container, not a disposed one.
 *    An unbound ref at redo time falls back to the scene root.
 *
 * 2. **Internal parents** for descendants — parents that are themselves
 *    among the just-created objects. Storing a raw SceneObject ref here
 *    is the stale-ref trap: the moment the first undo disposes the top-
 *    level (and transitively its descendants), those refs go dead. Redo
 *    would then try to reparent onto disposed instances and silently
 *    fall back to the scene root.
 *
 * The fix: callers pass live SceneObject refs at construction (both the
 * entry's own `object` and its `parent`). The constructor walks entries
 * in order, building a ref → index map, and resolves each `parent` into
 * a stable **tag** (`'top'`, `'external'`, or an index into the entry
 * array). On redo we rebuild objects in order and consult the tag
 * against the freshly created set, so we never trust a construction-time
 * ref after the first undo.
 *
 * ## Library references
 *
 * A library reference in the created set comes back as one (`referenceMarks.ts`):
 * the command records it as a reference, and redo runs its nested load again
 * (`TraceScene._hydrateReference`) after the rebuild. What a reference derives is
 * not the scene's, so the entries beneath one are left out however the caller
 * lists them (`buildChildEntries` hands over every descendant, derived ones
 * included), and they are not serialized. A reference that will not load again,
 * or whose load throws, does not fail the redo: it stands empty and a warning
 * names it.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { registerCommandCodec } from './CommandCodec';
import { isDerived, readMarks, restoreReferences, type NodeMarks } from './referenceMarks';

const CODEC_TYPE = 'CreateObject';

interface CreateObjectPayload {
    /** FQTP of the external parent (null = scene root). */
    parentPath: string | null;
    serializedData: SceneObjectData;
    /** What a rebuild of the top-level object cannot put back (see `referenceMarks.ts`). */
    marks?: NodeMarks;
    /** Already in stable-tag form — same shape as internal storage. */
    childEntries: Array<{ data: SceneObjectData; parent: 'top' | 'external' | number; marks?: NodeMarks }>;
}

/**
 * One descendant in a compound create. All three fields are live
 * SceneObject-related values at construction time; refs are consumed
 * during construction only and never stored raw.
 */
export interface CreateChildEntry {
    /**
     * The freshly-created descendant itself. Used by the command at
     * construction time to build a ref → index map so that later entries
     * can declare this one as their parent. Not stored after construction.
     */
    object: SceneObject;
    /** Serialized data used to recreate the object on redo. */
    data: SceneObjectData;
    /**
     * Parent at construction time. May be:
     *   - the top-level `createdObject` passed to the command, or
     *   - another entry earlier in the `childEntries` array, or
     *   - `null` for a scene-root fallback.
     *
     * The ref is only inspected during command construction; after that
     * it is translated into a stable internal tag, so stale refs post-
     * undo do not matter.
     */
    parent: SceneObject | null;
}

/**
 * Internal stable parent descriptor. One of:
 *   - `'top'`: parent is the top-level `createdObject`.
 *   - `'external'`: parent is null / scene root.
 *   - `number`: parent is `childEntries[number]` — i.e. another descendant.
 */
type ResolvedChildParent = 'top' | 'external' | number;

/**
 * Internal entry shape: serialized child data, stable parent tag, and the
 * shared identity ref for this descendant. The ref is allocated at
 * construction (or fromSerialized) time, rebound by `undo()` / `redo()`,
 * and shared with any other command that targets this child via the
 * scene's `objectIdentityRegistry`.
 */
interface InternalChildEntry {
    data: SceneObjectData;
    parent: ResolvedChildParent;
    identity: SceneObjectRef;
    /** What a rebuild from data cannot put back; absent for an ordinary node. */
    marks?: NodeMarks;
}

/**
 * Internal constructor input — used by both the public constructor (which
 * resolves live refs into this shape) and `fromSerialized` (which supplies
 * this shape directly from a payload). Keeping this typed means any new
 * field added to the command is caught at compile time regardless of which
 * entry point created the instance.
 */
interface CreateObjectCommandInit {
    traceScene: TraceScene;
    identity: SceneObjectRef;
    parentRef: SceneObjectRef | null;
    serializedData: SceneObjectData;
    marks?: NodeMarks;
    childEntries: InternalChildEntry[];
    label: string;
}

export class CreateObjectCommand implements UndoCommand {
    readonly label: string;
    private traceScene: TraceScene;
    /**
     * Shared identity ref for the top-level created object. Rebound across
     * undo/redo cycles via the scene's identity registry: bound after
     * construction → null after undo → bound to a fresh instance after
     * redo. Commands constructed *after* this one against the same logical
     * SceneObject share the same ref, so they keep targeting the live
     * instance even when this command swaps it out.
     */
    private identity: SceneObjectRef;
    /**
     * Parent of the top-level object, held as a shared identity ref (see
     * header §1): a sibling command in the same bundle (lazy rig wrap) can
     * delete and recreate the parent across undo/redo, and the shared ref is
     * what keeps this command pointing at the live instance. Null = scene root.
     */
    private parentRef: SceneObjectRef | null;
    private serializedData: SceneObjectData;
    /** What a rebuild of the top-level object cannot put back; absent for an ordinary node. */
    private marks: NodeMarks | undefined;
    /** Child data, resolved stable parent tag, and per-child identity ref. */
    private childEntries: InternalChildEntry[];

    constructor(
        createdObject: SceneObject,
        parent: SceneObject | null,
        serializedData: SceneObjectData,
        childEntries: CreateChildEntry[] = [],
    ) {
        const registry = createdObject.traceScene.objectIdentityRegistry;

        // Resolve each child's parent ref to a stable tag while the refs
        // are still live. Walk in order: earlier entries become valid
        // parent targets for later entries via the ref → index map.
        //
        // What stands beneath a library reference is derived, not the scene's:
        // it is left out here and comes back when the reference loads again.
        const refToIndex = new Map<SceneObject, number>();
        const derived = new Set<SceneObject>();
        const resolved: InternalChildEntry[] = [];
        for (const entry of childEntries) {
            if (isDerived(entry.parent, derived)) {
                derived.add(entry.object);
                continue;
            }
            const tag = resolveParent(entry.parent, createdObject, refToIndex);
            refToIndex.set(entry.object, resolved.length);
            const marks = readMarks(entry.object);
            resolved.push({
                data: structuredClone(entry.data),
                parent: tag,
                identity: registry.refFor(entry.object),
                ...(marks ? { marks } : {}),
            });
        }

        this.traceScene = createdObject.traceScene;
        this.identity = registry.refFor(createdObject);
        this.parentRef = parent ? registry.refFor(parent) : null;
        this.serializedData = structuredClone(serializedData);
        this.marks = readMarks(createdObject);
        this.childEntries = resolved;
        this.label = `Create ${createdObject.name}`;
    }

    private static _fromInit(init: CreateObjectCommandInit): CreateObjectCommand {
        // Route through a prototype bypass so we can set all private fields
        // explicitly — but every field is typed via CreateObjectCommandInit,
        // so adding a new private field triggers a compile error here.
        const cmd = Object.create(CreateObjectCommand.prototype) as CreateObjectCommand;
        const target = cmd as unknown as CreateObjectCommandInit & { label: string };
        target.traceScene = init.traceScene;
        target.identity = init.identity;
        target.parentRef = init.parentRef;
        target.serializedData = init.serializedData;
        target.marks = init.marks;
        target.childEntries = init.childEntries;
        target.label = init.label;
        return cmd;
    }

    /**
     * The shared identity ref of the created root: what every other command, and
     * the reference-identity registry, holds the object by.
     * @internal Read by {@link PlacedCopyCommand}, which lets a replacement
     * copy answer to the ref of the copy it replaces.
     */
    _rootIdentity(): SceneObjectRef {
        return this.identity;
    }

    /**
     * Make the created root answer to `ref` instead of a ref of its own. For a
     * command that has not run: the root it builds is another object's
     * successor, and must be the same identity to whoever held the first.
     * @internal
     */
    _adoptRootIdentity(ref: SceneObjectRef): void {
        this.identity = ref;
    }

    // Async for symmetry with `redo()` — this one could be synchronous
    // today, but declaring both halves as `Promise<void>` prevents a subtle
    // trap where a caller awaiting `cmd.undo()` in one code path silently
    // drops into a synchronous return in another.
    async undo(): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const selectedBefore = this.traceScene.getSelectedObject();
        this.traceScene.deleteSceneObject(obj);
        // Unbind every identity ref this command owns. Other commands that
        // share these refs will start no-op'ing at undo/redo time until
        // this command's redo runs and rebinds them to fresh instances.
        const registry = this.traceScene.objectIdentityRegistry;
        registry.rebind(this.identity, null);
        for (const entry of this.childEntries) {
            registry.rebind(entry.identity, null);
        }
        // If deleteSceneObject cleared the selection (it sat inside the
        // deleted subtree), land it on the live parent instead of leaving it
        // null, so selection-anchored context UIs (pages/states strips)
        // survive the undo. A selection outside the subtree — including no
        // selection at all — is untouched, hence the before/after check
        // rather than a bare null check.
        if (selectedBefore && !this.traceScene.getSelectedObject()) {
            this.traceScene.select(liveOrUndefined(this.parentRef?.get()) ?? null);
        }
    }

    /**
     * Construct a "pre-redo" instance from serialized data. The top-level
     * object hasn't been created yet (identity starts unbound); `redo()`
     * will materialize everything. Parent is resolved against the live
     * scene by path; stable child-parent tags are used as-is. Each child
     * also gets a fresh unbound ref that the first `redo()` will bind.
     */
    static fromSerialized(scene: TraceScene, payload: CreateObjectPayload): CreateObjectCommand {
        const parentObj = payload.parentPath ? scene.resolveSceneObject(payload.parentPath) : null;
        if (payload.parentPath && !parentObj) {
            throw new Error(`[CreateObjectCommand] Cannot resolve parent path "${payload.parentPath}"`);
        }
        // Unbound refs allocated up-front — the registry will pick them up
        // by instance the first time `redo()` runs and binds them. After
        // that first redo, any later command that targets the same instance
        // via `refFor()` gets this same shared ref.
        return CreateObjectCommand._fromInit({
            traceScene: scene,
            identity: scene.objectIdentityRegistry.allocateUnbound(),
            parentRef: parentObj ? scene.objectIdentityRegistry.refFor(parentObj) : null,
            serializedData: structuredClone(payload.serializedData),
            ...(payload.marks ? { marks: structuredClone(payload.marks) } : {}),
            childEntries: payload.childEntries.map(e => ({
                data: structuredClone(e.data),
                parent: e.parent,
                identity: scene.objectIdentityRegistry.allocateUnbound(),
                ...(e.marks ? { marks: structuredClone(e.marks) } : {}),
            })),
            label: `Create ${payload.serializedData.name}`,
        });
    }

    serialize(): SerializedCommand {
        const payload: CreateObjectPayload = {
            parentPath: this.parentRef?.get()?.getPath() ?? null,
            serializedData: structuredClone(this.serializedData),
            ...(this.marks ? { marks: structuredClone(this.marks) } : {}),
            childEntries: this.childEntries.map(e => ({
                data: structuredClone(e.data),
                parent: e.parent,
                ...(e.marks ? { marks: structuredClone(e.marks) } : {}),
            })),
        };
        return { type: CODEC_TYPE, data: payload };
    }

    async redo(): Promise<void> {
        // Resolve the external parent through the shared identity ref: a
        // bundled rig-wrap sibling may have recreated it since construction
        // (its rewrap rebinds this same ref). Unbound OR still pointing at a
        // DISPOSED node (a mis-ordered bundle resolved us before the sibling
        // rebind ran) → scene root, never a dead TransformNode.
        const topParent = liveOrUndefined(this.parentRef?.get());
        const topObj = await this.traceScene.createSceneObjectFromData(this.serializedData, topParent);

        const registry = this.traceScene.objectIdentityRegistry;
        registry.rebind(this.identity, topObj);

        // Recreate children in order. `parent` on each stored entry is a
        // stable tag produced at construction time, so lookups work
        // correctly across repeated undo/redo cycles.
        const createdChildren: SceneObject[] = [];
        for (const entry of this.childEntries) {
            const resolvedParent: SceneObject | undefined =
                entry.parent === 'top' ? topObj
                : entry.parent === 'external' ? topParent
                : createdChildren[entry.parent];
            const child = await this.traceScene.createSceneObjectFromData(entry.data, resolvedParent);
            createdChildren.push(child);
            registry.rebind(entry.identity, child);
        }

        // A reference comes back as one: marked, and loaded again.
        await restoreReferences(
            this.traceScene,
            [
                { object: topObj, marks: this.marks },
                ...createdChildren.map((object, i) => ({ object, marks: this.childEntries[i].marks })),
            ],
            'CreateObjectCommand',
            this.label,
        );

        // Not a node a second undo removed while the references above loaded.
        const live = liveOrUndefined(topObj);
        if (live) this.traceScene.select(live);
    }
}

/**
 * Translate a live SceneObject parent ref into a stable ResolvedChildParent
 * tag. Falls back to `'external'` for refs that are neither the top-level
 * created object nor a sibling earlier in the set — defensive handling for
 * callers that pass a parent outside the created tree.
 */
function resolveParent(
    parentRef: SceneObject | null,
    topLevel: SceneObject,
    refToIndex: Map<SceneObject, number>,
): ResolvedChildParent {
    if (parentRef === null) return 'external';
    if (parentRef === topLevel) return 'top';
    const idx = refToIndex.get(parentRef);
    if (idx !== undefined) return idx;
    return 'external';
}

/**
 * A ref target that is safe to parent under: live (not disposed). A shared
 * identity ref can transiently point at a disposed instance when a bundle is
 * mis-ordered (the sibling that rebinds it runs later) — fall back to the
 * scene root rather than silently attaching to a dead node.
 */
function liveOrUndefined(obj: SceneObject | null | undefined): SceneObject | undefined {
    if (!obj) return undefined;
    const node = (obj as { transform?: { isDisposed?: () => boolean } }).transform;
    if (typeof node?.isDisposed === 'function' && node.isDisposed()) return undefined;
    return obj;
}

registerCommandCodec(CODEC_TYPE, (data, scene): CreateObjectCommand => {
    return CreateObjectCommand.fromSerialized(scene, data as CreateObjectPayload);
});
